import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import clsx from 'clsx'
import { getComponentHeights } from '../utils'
import {
  ChartDumbbellDataSchema,
  ChartDumbbellUISchema,
  DumbbellDataItem,
  DumbbellTooltipConfig,
  DumbbellTooltipField,
  DumbbellShape,
} from './types'
import { ChartContainer, ChartLegendContent, ChartTooltip } from '@/components/shadcn/charts'
import { Bar, CartesianGrid, ComposedChart, Customized, XAxis, YAxis } from 'recharts'
import { normalizeRechartsData } from '../shared/rechartsDataUtils'

/**
 * Props for the custom tooltip component
 */
interface DumbbellTooltipProps {
  active?: boolean
  payload?: Array<{
    payload: Record<string, unknown>
    name?: string
  }>
  categoryKey: string
  startDataKey: string
  startLabel: string
  startUnit?: string
  endDataKey: string
  endLabel: string
  endUnit?: string
  tooltip?: DumbbellTooltipConfig
}

/**
 * Custom tooltip component for the dumbbell chart
 */
function DumbbellTooltipContent({
  active,
  payload,
  categoryKey,
  startDataKey,
  startLabel,
  startUnit,
  endDataKey,
  endLabel,
  endUnit,
  tooltip,
}: DumbbellTooltipProps) {
  if (!active || !payload?.length) {
    return null
  }

  const data = payload[0].payload

  // If custom fields are specified, use them; otherwise default to showing category and both values
  const fields: DumbbellTooltipField[] =
    tooltip?.fields && tooltip.fields.length > 0
      ? tooltip.fields
      : [
          { dataKey: categoryKey, label: 'Category' },
          { dataKey: startDataKey, label: startLabel, unit: startUnit },
          { dataKey: endDataKey, label: endLabel, unit: endUnit },
        ]

  return (
    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-offwhite px-2.5 py-1.5 text-xs shadow-xl">
      <div className="grid gap-1">
        {fields.map((field) => {
          const value = data[field.dataKey]
          if (value === undefined) return null
          return (
            <div key={field.dataKey} className="flex justify-between gap-4">
              <span className="text-muted-foreground">{field.label}</span>
              <span className="font-mono font-medium tabular-nums text-foreground">
                {String(value)}
                {field.unit && <span className="ml-0.5 text-muted-foreground">{field.unit}</span>}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Props for the dumbbell chart custom renderer
 */
interface DumbbellRendererProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  xAxisMap?: Record<string, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  yAxisMap?: Record<string, any>
  data: DumbbellDataItem[]
  categoryKey: string
  startDataKey: string
  startColor: string
  startShape: DumbbellShape
  startSize: number
  endDataKey: string
  endColor: string
  endShape: DumbbellShape
  endSize: number
  connectorColor: string
  connectorPositiveColor?: string
  connectorNegativeColor?: string
  connectorNeutralColor?: string
  connectorStrokeWidth: number
  connectorStrokeDasharray?: string
  isHorizontal: boolean
}

/**
 * Renders a shape at the given position
 */
function renderShape(
  shape: DumbbellShape,
  cx: number,
  cy: number,
  size: number,
  fill: string,
  key: string
): React.ReactNode {
  const r = Math.sqrt(size / Math.PI) // Convert area to radius for circle
  const halfSize = r * 1.2 // Approximate size for other shapes

  switch (shape) {
    case 'circle':
      return <circle key={key} cx={cx} cy={cy} r={r} fill={fill} />
    case 'square':
      return (
        <rect key={key} x={cx - halfSize} y={cy - halfSize} width={halfSize * 2} height={halfSize * 2} fill={fill} />
      )
    case 'diamond':
      return (
        <polygon
          key={key}
          points={`${cx},${cy - halfSize} ${cx + halfSize},${cy} ${cx},${cy + halfSize} ${cx - halfSize},${cy}`}
          fill={fill}
        />
      )
    case 'triangle':
      return (
        <polygon
          key={key}
          points={`${cx},${cy - halfSize} ${cx + halfSize},${cy + halfSize} ${cx - halfSize},${cy + halfSize}`}
          fill={fill}
        />
      )
    case 'star': {
      // 5-pointed star
      const outerR = halfSize
      const innerR = halfSize * 0.4
      const points: string[] = []
      for (let i = 0; i < 10; i++) {
        const radius = i % 2 === 0 ? outerR : innerR
        const angle = (Math.PI / 5) * i - Math.PI / 2
        points.push(`${cx + radius * Math.cos(angle)},${cy + radius * Math.sin(angle)}`)
      }
      return <polygon key={key} points={points.join(' ')} fill={fill} />
    }
    case 'cross': {
      const armWidth = halfSize * 0.4
      return (
        <path
          key={key}
          d={`M${cx - armWidth},${cy - halfSize} h${armWidth * 2} v${halfSize - armWidth} h${halfSize - armWidth} v${armWidth * 2} h${-(halfSize - armWidth)} v${halfSize - armWidth} h${-armWidth * 2} v${-(halfSize - armWidth)} h${-(halfSize - armWidth)} v${-armWidth * 2} h${halfSize - armWidth} Z`}
          fill={fill}
        />
      )
    }
    case 'wye':
      // Y shape
      return (
        <path
          key={key}
          d={`M${cx},${cy} L${cx},${cy + halfSize} M${cx},${cy} L${cx - halfSize * 0.866},${cy - halfSize * 0.5} M${cx},${cy} L${cx + halfSize * 0.866},${cy - halfSize * 0.5}`}
          stroke={fill}
          strokeWidth={halfSize * 0.4}
          strokeLinecap="round"
          fill="none"
        />
      )
    default:
      return <circle key={key} cx={cx} cy={cy} r={r} fill={fill} />
  }
}

/**
 * Determines the connector color based on the change direction
 */
function getConnectorColor(
  startValue: number,
  endValue: number,
  defaultColor: string,
  positiveColor?: string,
  negativeColor?: string,
  neutralColor?: string
): string {
  // If no conditional colors are specified, use the default
  if (!positiveColor && !negativeColor) {
    return defaultColor
  }

  const change = endValue - startValue

  if (change > 0 && positiveColor) {
    return positiveColor
  } else if (change < 0 && negativeColor) {
    return negativeColor
  } else if (change === 0 && neutralColor) {
    return neutralColor
  }

  return defaultColor
}

/**
 * Custom component to render the complete dumbbell chart (lines + dots)
 */
function DumbbellRenderer({
  xAxisMap,
  yAxisMap,
  data,
  categoryKey,
  startDataKey,
  startColor,
  startShape,
  startSize,
  endDataKey,
  endColor,
  endShape,
  endSize,
  connectorColor,
  connectorPositiveColor,
  connectorNegativeColor,
  connectorNeutralColor,
  connectorStrokeWidth,
  connectorStrokeDasharray,
  isHorizontal,
}: DumbbellRendererProps) {
  if (!xAxisMap || !yAxisMap) return null

  const xAxis = Object.values(xAxisMap)[0]
  const yAxis = Object.values(yAxisMap)[0]

  if (!xAxis || !yAxis) return null

  // Determine which axis is category and which is value based on orientation
  const categoryAxis = isHorizontal ? yAxis : xAxis
  const valueAxis = isHorizontal ? xAxis : yAxis

  // Get the bandwidth for centering on category positions
  const bandwidth = categoryAxis.bandSize || categoryAxis.bandwidth || 0
  const halfBandwidth = bandwidth / 2

  return (
    <g className="dumbbell-chart-elements">
      {data.map((item, index) => {
        const categoryValue = item[categoryKey]
        const startValue = Number(item[startDataKey])
        const endValue = Number(item[endDataKey])

        if (isNaN(startValue) || isNaN(endValue)) return null

        // Get the category position and center it
        const categoryPos = categoryAxis.scale(categoryValue)
        if (categoryPos === undefined) return null
        const centeredCategoryPos = categoryPos + halfBandwidth

        // Get value positions
        const startValuePos = valueAxis.scale(startValue)
        const endValuePos = valueAxis.scale(endValue)

        // Calculate coordinates based on orientation
        const startX = isHorizontal ? startValuePos : centeredCategoryPos
        const startY = isHorizontal ? centeredCategoryPos : startValuePos
        const endX = isHorizontal ? endValuePos : centeredCategoryPos
        const endY = isHorizontal ? centeredCategoryPos : endValuePos

        // Determine the connector color based on change direction
        const lineColor = getConnectorColor(
          startValue,
          endValue,
          connectorColor,
          connectorPositiveColor,
          connectorNegativeColor,
          connectorNeutralColor
        )

        return (
          <g key={`dumbbell-${index}`}>
            {/* Connector line (rendered first, behind dots) */}
            <line
              x1={startX}
              y1={startY}
              x2={endX}
              y2={endY}
              stroke={lineColor}
              strokeWidth={connectorStrokeWidth}
              strokeDasharray={connectorStrokeDasharray}
            />
            {/* Start point */}
            {renderShape(startShape, startX, startY, startSize, startColor, `start-${index}`)}
            {/* End point */}
            {renderShape(endShape, endX, endY, endSize, endColor, `end-${index}`)}
          </g>
        )
      })}
    </g>
  )
}

/**
 * Wraps text into lines at word boundaries, fitting within maxWidthPx.
 * If the text exceeds maxLines, the last visible line is truncated with "...".
 */
function wrapTextToLines(text: string, maxWidthPx: number, maxLines: number): string[] {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) return [text]
  context.font = '12px sans-serif'

  // If text fits in a single line, return as-is
  if (context.measureText(text).width <= maxWidthPx) {
    return [text]
  }

  const words = text.split(/\s+/)
  const lines: string[] = []
  let currentLine = ''

  for (let i = 0; i < words.length; i++) {
    const word = words[i]
    const testLine = currentLine ? `${currentLine} ${word}` : word

    if (context.measureText(testLine).width > maxWidthPx && currentLine) {
      // Word doesn't fit on the current line
      if (lines.length + 1 >= maxLines) {
        // Last allowed line – truncate with ellipsis
        let truncated = currentLine + '...'
        while (context.measureText(truncated).width > maxWidthPx && truncated.length > 4) {
          truncated = truncated.slice(0, -4) + '...'
        }
        lines.push(truncated)
        return lines
      }
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = testLine
    }
  }

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines
}

export function ChartDumbbellContent({
  uiSchema,
  dataSchema,
  tileFilters,
}: {
  uiSchema: ChartDumbbellUISchema
  dataSchema: ChartDumbbellDataSchema
  tileFilters?: React.ReactNode
}) {
  const { title = undefined, description = undefined, chartConfig, emphasizeHeader } = uiSchema
  const {
    orientation,
    categoryAxis,
    valueAxis,
    startPoint,
    endPoint,
    connector,
    tooltip,
    config,
    showGrid = true,
    fixedSize = false,
    lineClampCategoryLabels,
  } = chartConfig
  const { data: rawData } = dataSchema
  const { data, mapDataKey } = normalizeRechartsData(rawData, [
    categoryAxis.dataKey,
    startPoint.dataKey,
    endPoint.dataKey,
    ...(tooltip?.fields?.map((field) => field.dataKey) ?? []),
  ])
  const normalizedCategoryKey = mapDataKey(categoryAxis.dataKey)
  const normalizedStartKey = mapDataKey(startPoint.dataKey)
  const normalizedEndKey = mapDataKey(endPoint.dataKey)
  const normalizedTooltip = tooltip
    ? {
        ...tooltip,
        fields: tooltip.fields?.map((field) => ({
          ...field,
          dataKey: mapDataKey(field.dataKey),
        })),
      }
    : undefined
  const hasHeader = !!title && !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)

  const isHorizontal = orientation === 'horizontal'

  // Connector styling with defaults
  const connectorColor = connector?.color ?? '#080808'
  const connectorPositiveColor = connector?.positiveColor
  const connectorNegativeColor = connector?.negativeColor
  const connectorNeutralColor = connector?.neutralColor
  const connectorStrokeWidth = connector?.strokeWidth ?? 2
  const connectorStrokeDasharray = connector?.strokeDasharray

  // Calculate domain for value axis to include all values with some padding
  const allValues = data.flatMap((item) => [Number(item[normalizedStartKey]), Number(item[normalizedEndKey])])
  const minValue = Math.min(...allValues)
  const maxValue = Math.max(...allValues)
  const padding = (maxValue - minValue) * 0.1
  const domainMin =
    valueAxis.domain?.[0] === 'auto' ? minValue - padding : (valueAxis.domain?.[0] ?? minValue - padding)
  const domainMax =
    valueAxis.domain?.[1] === 'auto' ? maxValue + padding : (valueAxis.domain?.[1] ?? maxValue + padding)

  // Calculate dynamic width for category axis based on longest label
  const calculateAxisWidth = () => {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) return 120
    context.font = '12px sans-serif'
    const maxWidth = data.reduce((max, item) => {
      const label = String(item[normalizedCategoryKey] || '')
      const width = context.measureText(label).width
      return Math.max(max, width)
    }, 0)
    return Math.max(maxWidth + 20, 80)
  }

  const categoryAxisWidth = calculateAxisWidth()

  // Custom tick renderers for category axis labels with line clamping (native SVG text)
  const TICK_LINE_HEIGHT = 16

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderClampedYAxisTick = (props: any) => {
    const { x, y, payload } = props
    const lines = wrapTextToLines(String(payload.value), x, lineClampCategoryLabels!)

    return (
      <text x={x} y={y} fontSize={12} fill="hsl(var(--muted-foreground))">
        <title>{String(payload.value)}</title>
        {lines.map((line, i) => (
          <tspan
            key={i}
            x={x}
            dy={i === 0 ? -(lines.length - 1) * (TICK_LINE_HEIGHT / 2) : TICK_LINE_HEIGHT}
            textAnchor="end"
            dominantBaseline="central"
          >
            {line}
          </tspan>
        ))}
      </text>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderClampedXAxisTick = (props: any) => {
    const { x, y, payload } = props
    const labelLength = categoryAxisWidth > 150 ? categoryAxisWidth * 0.65 : 80
    const lines = wrapTextToLines(String(payload.value), labelLength, lineClampCategoryLabels!)

    return (
      <text
        x={x}
        y={y}
        fontSize={12}
        fill="hsl(var(--muted-foreground))"
        transform={`rotate(-45, ${x}, ${y})`}
        textAnchor="end"
      >
        <title>{String(payload.value)}</title>
        {lines.map((line, i) => (
          <tspan key={i} x={x} dy={i === 0 ? 0 : TICK_LINE_HEIGHT} dominantBaseline="central">
            {line}
          </tspan>
        ))}
      </text>
    )
  }

  // Fixed size calculation: space per category row for comfortable viewing
  const FIXED_SIZE_PER_CATEGORY = 30 // pixels per category item
  const CHART_PADDING = 80 // Additional padding for axes, legend, margins
  const dataRowCount = data.length

  // Calculate fixed dimensions based on orientation
  const fixedHeight = isHorizontal ? dataRowCount * FIXED_SIZE_PER_CATEGORY + CHART_PADDING : undefined
  const fixedWidth = !isHorizontal ? dataRowCount * FIXED_SIZE_PER_CATEGORY + CHART_PADDING : undefined

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>
        </ComponentHeader>
      )}
      <div
        className={clsx({
          'pt-5': hasHeader,
          'overflow-y-auto': fixedSize && isHorizontal,
          'overflow-x-auto': fixedSize && !isHorizontal,
        })}
        style={{ height: contentHeight }}
      >
        <ChartContainer
          className={clsx('mx-auto', {
            'h-full w-full': !fixedSize,
            'w-full': fixedSize && isHorizontal,
            'h-full': fixedSize && !isHorizontal,
          })}
          config={config}
          legend={
            <ChartLegendContent
              className="pt-3"
              items={[
                { key: startPoint.dataKey, label: startPoint.label, color: startPoint.color },
                { key: endPoint.dataKey, label: endPoint.label, color: endPoint.color },
              ]}
            />
          }
          style={{
            ...(fixedSize && isHorizontal && fixedHeight ? { height: fixedHeight, minHeight: fixedHeight } : {}),
            ...(fixedSize && !isHorizontal && fixedWidth ? { width: fixedWidth, minWidth: fixedWidth } : {}),
          }}
        >
          <ComposedChart
            data={data}
            layout={isHorizontal ? 'vertical' : 'horizontal'}
            margin={{
              top: 20,
              right: 30,
              bottom: 20,
              left: 20,
            }}
          >
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}

            {isHorizontal ? (
              <>
                {/* Horizontal layout: Y axis is category, X axis is value */}
                <YAxis
                  dataKey={normalizedCategoryKey}
                  type="category"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  width={categoryAxisWidth > 150 ? categoryAxisWidth * 0.65 : 100}
                  interval={0}
                  {...(lineClampCategoryLabels ? { tick: renderClampedYAxisTick } : {})}
                />
                <XAxis
                  type="number"
                  domain={[domainMin, domainMax]}
                  tickLine={false}
                  axisLine={false}
                  label={valueAxis.label ? { value: valueAxis.label, position: 'bottom', offset: 0 } : undefined}
                />
              </>
            ) : (
              <>
                {/* Vertical layout: X axis is category, Y axis is value */}
                <XAxis
                  dataKey={normalizedCategoryKey}
                  type="category"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  interval={0}
                  height={categoryAxisWidth > 150 ? categoryAxisWidth * 0.65 : 80}
                  {...(lineClampCategoryLabels
                    ? {
                        tick: renderClampedXAxisTick,
                      }
                    : {
                        angle: -45,
                        textAnchor: 'end' as const,
                      })}
                />
                <YAxis
                  type="number"
                  domain={[domainMin, domainMax]}
                  tickLine={false}
                  axisLine={false}
                  label={valueAxis.label ? { value: valueAxis.label, angle: -90, position: 'insideLeft' } : undefined}
                />
              </>
            )}

            {/* Tooltip */}
            <ChartTooltip
              cursor={false}
              content={
                <DumbbellTooltipContent
                  categoryKey={normalizedCategoryKey}
                  startDataKey={normalizedStartKey}
                  startLabel={startPoint.label}
                  startUnit={valueAxis.unit}
                  endDataKey={normalizedEndKey}
                  endLabel={endPoint.label}
                  endUnit={valueAxis.unit}
                  tooltip={normalizedTooltip}
                />
              }
            />

            {/* Invisible bar for tooltip hover detection */}
            <Bar
              dataKey={normalizedStartKey}
              fill="transparent"
              stroke="transparent"
              barSize={30}
              isAnimationActive={false}
            />

            {/* Custom renderer for dumbbell elements (lines + dots) */}
            <Customized
              component={(props: {
                xAxisMap?: DumbbellRendererProps['xAxisMap']
                yAxisMap?: DumbbellRendererProps['yAxisMap']
              }) => (
                <DumbbellRenderer
                  xAxisMap={props.xAxisMap}
                  yAxisMap={props.yAxisMap}
                  data={data}
                  categoryKey={normalizedCategoryKey}
                  startDataKey={normalizedStartKey}
                  startColor={startPoint.color}
                  startShape={startPoint.shape ?? 'circle'}
                  startSize={startPoint.size ?? 60}
                  endDataKey={normalizedEndKey}
                  endColor={endPoint.color}
                  endShape={endPoint.shape ?? 'circle'}
                  endSize={endPoint.size ?? 60}
                  connectorColor={connectorColor}
                  connectorPositiveColor={connectorPositiveColor}
                  connectorNegativeColor={connectorNegativeColor}
                  connectorNeutralColor={connectorNeutralColor}
                  connectorStrokeWidth={connectorStrokeWidth}
                  connectorStrokeDasharray={connectorStrokeDasharray}
                  isHorizontal={isHorizontal}
                />
              )}
            />
          </ComposedChart>
        </ChartContainer>
      </div>
    </div>
  )
}
